/**
 * Collection listing (sections/main-collection-listing.liquid)
 * <collection-listing>
 *  - Sticky bar sits right under the header (measures the header height).
 *  - Grid switch: "spot view" (dense grid of small images); remembered for the visit.
 *  - Filters drawer (<dialog>): Apply, Clear all, active filter pills and Sort update the products
 *    without a page reload (Section Rendering API) and keep the URL in sync (Back button works).
 *  - Load more: appends the next page of products.
 * Everything is event-delegated, so it keeps working after the content is replaced.
 * Without JavaScript the same controls are plain links and a GET form.
 */
(() => {
  if (customElements.get('collection-listing')) return;

  const SPOT_KEY = 'theme:collection-listing:spot';

  class CollectionListing extends HTMLElement {
    #controller = new AbortController();
    /** @type {AbortController | null} */
    #fetchController = null;
    /** @type {ResizeObserver | null} */
    #headerObserver = null;

    connectedCallback() {
      const { signal } = this.#controller;

      this.addEventListener('click', this.#onClick.bind(this), { signal });
      this.addEventListener('change', this.#onChange.bind(this), { signal });
      this.addEventListener('submit', this.#onSubmit.bind(this), { signal });
      window.addEventListener('popstate', () => this.#render(window.location.href, false), { signal });

      this.#watchHeader();
      this.#watchStuck();
      this.#setSpotView(this.#readSpot(), false);
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#controller = new AbortController();
      this.#fetchController?.abort();
      this.#headerObserver?.disconnect();
    }

    /** Keep the sticky bar right below the header, whatever its height. */
    #watchHeader() {
      const header = document.querySelector('.shopify-section-header');
      if (!header) return;
      const update = () => this.style.setProperty('--collection-listing-top', `${Math.round(header.getBoundingClientRect().height)}px`);
      update();
      if ('ResizeObserver' in window) {
        this.#headerObserver = new ResizeObserver(update);
        this.#headerObserver.observe(header);
      }
    }

    /** Marks the bar .is-stuck while it sits pinned under the header (the title fades out on phones). */
    #watchStuck() {
      const { signal } = this.#controller;
      let frame = 0;
      const update = () => {
        frame = 0;
        const bar = this.querySelector('[data-listing-bar]');
        if (!bar) return;
        const top = parseFloat(getComputedStyle(this).getPropertyValue('--collection-listing-top')) || 0;
        const stuck = this.getBoundingClientRect().top < top - 1 && bar.getBoundingClientRect().top <= top + 1;
        bar.classList.toggle('is-stuck', stuck);
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      update();
    }

    /** @param {MouseEvent} event */
    #onClick(event) {
      const target = /** @type {HTMLElement} */ (event.target);

      if (target.closest('[data-listing-switch]')) {
        this.#setSpotView(!this.classList.contains('collection-listing--spot'), true);
        return;
      }

      if (target.closest('[data-listing-open]')) {
        this.#drawer()?.showModal();
        return;
      }

      const drawer = this.#drawer();
      if (target.closest('[data-listing-close]') || (drawer && target === drawer)) {
        drawer?.close();
        return;
      }

      const more = target.closest('[data-listing-more]');
      if (more instanceof HTMLAnchorElement) {
        event.preventDefault();
        this.#loadMore(more);
        return;
      }

      const link = target.closest('[data-listing-link]');
      if (link instanceof HTMLAnchorElement) {
        event.preventDefault();
        this.#render(link.href);
      }
    }

    /** @param {Event} event */
    #onChange(event) {
      const select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.matches('[data-listing-sort]')) return;
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      this.#render(url.toString());
    }

    /** @param {SubmitEvent} event */
    #onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-listing-form]')) return;
      event.preventDefault();

      const params = new URLSearchParams();
      for (const [name, value] of new FormData(form)) {
        if (typeof value === 'string' && value.trim() !== '') params.append(name, value);
      }
      // Keep the desktop sort when the drawer's sort is hidden
      if (!params.has('sort_by')) {
        const sort = this.querySelector('[data-listing-sort]');
        if (sort instanceof HTMLSelectElement) params.set('sort_by', sort.value);
      }
      const url = new URL(form.action, window.location.origin);
      url.search = params.toString();
      this.#render(url.toString());
    }

    #drawer() {
      const drawer = this.querySelector('[data-listing-drawer]');
      return drawer instanceof HTMLDialogElement ? drawer : null;
    }

    /**
     * @param {boolean} on
     * @param {boolean} save
     */
    #setSpotView(on, save) {
      this.classList.toggle('collection-listing--spot', on);
      this.querySelector('[data-listing-switch]')?.setAttribute('aria-pressed', String(on));
      if (!save) return;
      try {
        sessionStorage.setItem(SPOT_KEY, on ? '1' : '0');
      } catch {
        // storage blocked - the view just isn't remembered
      }
      this.#scrollToTop();
    }

    /** Visitor's choice this visit; otherwise the section's "Start in spot view" setting. */
    #readSpot() {
      const fallback = this.hasAttribute('data-spot-default');
      try {
        const saved = sessionStorage.getItem(SPOT_KEY);
        return saved === null ? fallback : saved === '1';
      } catch {
        return fallback;
      }
    }

    #scrollToTop() {
      const top = this.getBoundingClientRect().top + window.scrollY - (parseFloat(getComputedStyle(this).getPropertyValue('--collection-listing-top')) || 0);
      if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
    }

    /**
     * @param {string} href
     * @returns {Promise<Document>}
     */
    async #fetchSection(href) {
      this.#fetchController?.abort();
      this.#fetchController = new AbortController();
      const url = new URL(href, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId || '');
      const response = await fetch(url, { signal: this.#fetchController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    /**
     * Replaces the bar, grid and drawer with the filtered / sorted result.
     * @param {string} href
     * @param {boolean} [push]
     */
    async #render(href, push = true) {
      this.#drawer()?.close();
      this.classList.add('is-loading');
      try {
        const html = await this.#fetchSection(href);
        const next = html.querySelector('collection-listing');
        if (!next) throw new Error('section not found');
        this.replaceChildren(...next.childNodes);
        window.dispatchEvent(new Event('scroll'));
        this.querySelector('[data-listing-switch]')?.setAttribute('aria-pressed', String(this.classList.contains('collection-listing--spot')));

        if (push) {
          const clean = new URL(href, window.location.origin);
          clean.searchParams.delete('section_id');
          window.history.pushState({}, '', clean.toString());
        }
        this.#scrollToTop();
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = href; // fall back to a normal page load
      } finally {
        this.classList.remove('is-loading');
      }
    }

    /** @param {HTMLAnchorElement} button */
    async #loadMore(button) {
      button.classList.add('is-loading');
      try {
        const html = await this.#fetchSection(button.href);
        const items = html.querySelectorAll('[data-listing-grid] > [data-listing-item]');
        this.querySelector('[data-listing-grid]')?.append(...items);

        const nextMore = html.querySelector('[data-listing-more]');
        if (nextMore instanceof HTMLAnchorElement) {
          button.href = nextMore.href;
          button.classList.remove('is-loading');
        } else {
          button.closest('.collection-listing__more')?.remove();
        }
      } catch (error) {
        if (error.name !== 'AbortError') window.location.href = button.href;
      }
    }
  }

  customElements.define('collection-listing', CollectionListing);
})();
